import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

export const BridgeSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useTranslation();

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-background relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 flex items-center justify-center opacity-5">
        <div className="w-[600px] h-[600px] border-2 border-foreground rounded-full" />
        <div className="absolute w-[400px] h-[400px] border-2 border-foreground rounded-full" />
        <div className="absolute w-[200px] h-[200px] border-2 border-foreground rounded-full" />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground mb-6">
            {t('bridge.title')}
          </h2>
          <p className="text-base lg:text-lg text-muted-foreground">
            {t('bridge.subtitle')}
          </p>
        </motion.div>

        {/* Bridge Illustration */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative max-w-4xl mx-auto mb-16"
        >
          <svg viewBox="0 0 800 300" className="w-full h-auto">
            {/* Bridge Base */}
            <defs>
              <linearGradient id="bridgeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="1" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              </linearGradient>
            </defs>

            {/* Bridge Arc */}
            <motion.path
              d="M 50 250 Q 400 50 750 250"
              fill="none"
              stroke="url(#bridgeGradient)"
              strokeWidth="6"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={isInView ? { pathLength: 1 } : {}}
              transition={{ duration: 1.5, delay: 0.5 }}
            />

            {/* Support Lines */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 1 }}
            >
              {[100, 200, 300, 400, 500, 600, 700].map((x, i) => (
                <line
                  key={i}
                  x1={x}
                  y1={250}
                  x2={x}
                  y2={150 + Math.abs(x - 400) * 0.15}
                  stroke="hsl(var(--primary))"
                  strokeWidth="2"
                  strokeOpacity="0.3"
                />
              ))}
            </motion.g>

            {/* Left Platform - Enterprises */}
            <motion.g
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <rect x="20" y="240" width="100" height="50" rx="8" fill="hsl(var(--secondary))" />
              <text x="70" y="270" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="12" fontWeight="500">
                {t('bridge.enterprises')}
              </text>
            </motion.g>

            {/* Right Platform - Retirees */}
            <motion.g
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <rect x="680" y="240" width="100" height="50" rx="8" fill="hsl(var(--secondary))" />
              <text x="730" y="270" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="12" fontWeight="500">
                {t('bridge.retirees')}
              </text>
            </motion.g>

            {/* Center Logo */}
            <motion.g
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 1.5, type: "spring" }}
            >
              <circle cx="400" cy="100" r="45" fill="hsl(var(--primary))" />
              <text x="400" y="95" textAnchor="middle" fill="hsl(var(--primary-foreground))" fontSize="16" fontWeight="bold" fontFamily="serif">
                sfretire
              </text>
              <text x="400" y="112" textAnchor="middle" fill="hsl(var(--primary-foreground))" fontSize="10">
                .com
              </text>
            </motion.g>
          </svg>
        </motion.div>

        {/* Two Columns */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* For Businesses */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="card-elevated p-8 text-center"
          >
            <h3 className="text-xl font-serif font-semibold text-foreground mb-3">
              {t('bridge.forBusinesses.title')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t('bridge.forBusinesses.description')}
            </p>
            <Button variant="gold" asChild>
              <Link to="/entreprises" className="gap-2">
                {t('hero.cta1')}
                <ArrowRight size={16} />
              </Link>
            </Button>
          </motion.div>

          {/* For Retirees */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="card-elevated p-8 text-center"
          >
            <h3 className="text-xl font-serif font-semibold text-foreground mb-3">
              {t('bridge.forRetirees.title')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t('bridge.forRetirees.description')}
            </p>
            <Button variant="outline-gold" asChild>
              <Link to="/retraites" className="gap-2">
                {t('hero.cta2')}
                <ArrowRight size={16} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
